'use client';

import { useContext, useEffect } from 'react';
import { AppContext } from '@/context/AppContext';
import { motion } from 'framer-motion';
import Link from 'next/link';
import { HomeData } from '@/data/types';  // Direkter Import
import { SkillRotator } from './SkillRotator';
import { useLocalePath } from '@/i18n/LangContext';

interface HeroSectionProps {
  homeData: HomeData;  // Direkte Verwendung des Typs
}

export function HeroSection({ homeData }: HeroSectionProps) {
  const { setActivePage } = useContext(AppContext);
  const lp = useLocalePath();

  useEffect(() => {
    setActivePage('home');
  }, [setActivePage]);

  return (
    <div className="mb-16 md:mb-24">
      <motion.div
        className="inline-block bg-[#0C0B32]/40 backdrop-blur-sm border border-[#88ffff]/20 px-3 py-1 rounded-lg mb-6"
        initial={{ opacity: 0, x: -20 }}
        animate={{ opacity: 1, x: 0 }}
        transition={{ duration: 0.6 }}
      >
        <span className="text-[#88ffff] text-sm">{homeData.welcome}</span>
      </motion.div>

      <motion.h1
        className="text-4xl md:text-7xl font-bold mb-3"
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.8, delay: 0.2 }}
      >
        {homeData.name}
      </motion.h1>

      {/* Die einzige wirklich dynamische Komponente die ClientSide sein muss */}
      <SkillRotator skills={homeData.skills} />

      <motion.p
        className="text-md md:text-lg text-white/80 max-w-2xl mb-10"
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.8, delay: 0.4 }}
      >
        {homeData.intro}
      </motion.p>

      <motion.div
        className="flex flex-wrap gap-4"
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.8, delay: 0.6 }}
      >
        <Link href={lp('/work')}>
          <motion.button
            className="px-8 py-4 rounded-lg bg-[#0C0B32] text-[#88ffff] border border-[#88ffff]/30 font-medium"
            whileHover={{
              scale: 1.05,
              backgroundColor: 'rgba(15, 15, 70, 1)'
            }}
            whileTap={{ scale: 0.97 }}
          >
            {homeData.buttons.work}
          </motion.button>
        </Link>
        <Link href={lp('/contact')}>
          <motion.button
            className="px-8 py-4 rounded-lg bg-transparent border border-white/30 text-white font-medium"
            whileHover={{
              scale: 1.05,
              borderColor: 'rgba(136, 255, 255, 0.5)'
            }}
            whileTap={{ scale: 0.97 }}
          >
            {homeData.buttons.contact}
          </motion.button>
        </Link>
      </motion.div>
    </div>
  );
}